/**
 * frontend/pages/InviteLanding.jsx — /invite/:referral_code (player/creator referral links).
 *
 * Saves the normalised code (lib/referral: { code, savedAt }, 30 days) so it survives the auth
 * flow, checks it with GET /api/users/referrer/:code, and shows who invited the visitor. An
 * unknown code is removed and the page reads as a plain invitation. "Join" goes straight to
 * /login, where the same banner shows and the code rides along in signInWithOtp metadata.
 *
 * Styles: InviteLanding.css (shared with ArtistInviteLanding, which uses the same invite-* classes).
 */

import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import { normalizeReferralCode, saveReferralCode } from '../lib/referral';
import { useInviter } from '../hooks/useInviter';
import './InviteLanding.css';

const PERSONAS = [
  { icon: '🎤', role: 'Creator', line: 'get discovered' },
  { icon: '🎧', role: 'Player', line: 'build your roster' },
  { icon: '💎', role: 'Collector', line: 'back artists early' },
];

export default function InviteLanding({ session }) {
  const { referral_code } = useParams();
  const navigate = useNavigate();
  const code = normalizeReferralCode(referral_code);
  // Saved before the check resolves; useInviter removes it again if the backend says it's unknown.
  useEffect(() => { saveReferralCode(referral_code); }, [referral_code]);
  const inviter = useInviter(code);

  if (session) {
    return (
      <div className="invite-screen">
        <div className="invite-card">
          <div className="invite-logo"><BrandMark /></div>
          <h1 className="invite-title">You're already in</h1>
          <p className="invite-desc">Head to your dashboard to see your SOUND balance and recruits.</p>
          <button type="button" className="invite-btn" onClick={() => navigate('/')}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  const invitedBy = inviter.status === 'valid' && inviter.displayName;

  return (
    <div className="invite-screen">
      <div className="invite-card">
        <div className="invite-logo"><BrandMark /></div>
        {invitedBy && <p className="invite-by"><strong>{invitedBy}</strong> invited you</p>}
        <h1 className="invite-title">You've been invited to<br />Big Creative</h1>
        <p className="invite-desc">
          SoundDAO is a music fan game where you earn SOUND by discovering artists,
          recruiting creators, and building your roster.
        </p>
        <div className="invite-perks" role="list">
          {PERSONAS.map((p) => (
            <div key={p.role} className="invite-perk" role="listitem">
              <span className="invite-perk-icon" aria-hidden="true">{p.icon}</span>
              <span className="invite-perk-text"><strong>Join as a {p.role}</strong> — {p.line}</span>
            </div>
          ))}
        </div>
        <button type="button" className="invite-btn" onClick={() => navigate('/login')}>
          Join Big Creative →
        </button>
        <p className="invite-note">No password. No wallet required. Just your email.</p>
      </div>
    </div>
  );
}
